import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Book,
  Globe,
  FileText,
  Video,
  Archive,
  Plus,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Library,
  Users,
  Star,
  Filter,
  Pencil,
  Trash2,
  Shield,
  Building2,
  X,
  Eye,
  Download
} from 'lucide-react';
import { authenticatedFetch, getUser } from '@/lib/auth';
import LicenseDisclaimerModal from './LicenseDisclaimerModal';
import OwnLicenseRequiredModal from './OwnLicenseRequiredModal';
import { LicenseFormData } from './InlineLicenseForm';

interface SharedLiterature {
  id: string;
  title: string;
  original_url: string | null;
  isbn: string | null;
  doi: string | null;
  author: string | null;
  publisher: string | null;
  publication_year: number | null;
  language: string;
  description: string | null;
  literature_type: string;
  download_status: string;
  local_file_path: string | null;
  usage_count: number;
  quality_rating: number | null;
  categories: string[];
  tags: string[];
  license_type: string | null;
  cc_variant: string | null;
  allows_distribution: boolean | null;
  allows_modification: boolean | null;
  allows_commercial_use: boolean | null;
  requires_attribution: boolean | null;
  attribution_text: string | null;
  rights_holder: string | null;
  access_level: 'open' | 'restricted' | 'unknown';
  created_at: string;
  created_by: string | null;
  created_by_org_id: string | null;
  created_by_org_name: string | null;
  // Eier — autoritativ for tilgang og kontakt
  owner_member_id: string | null;
  owner_member_name: string | null;
  owner_contact_email: string | null;
  owner_study_association_id: string | null;
  owner_study_association_name: string | null;
  creator_name: string | null;
  creator_email: string | null;
  current_version_number: number;
  link_count: number;
}

interface SharingRulesResponse {
  canShare: boolean;
  requiresDisclaimer: boolean;
  requiresOwnLicense: boolean;
  inheritLicense: boolean;
  reason?: string;
  warnings: string[];
  license?: {
    license_type: string;
    cc_variant?: string | null;
    requires_attribution?: boolean;
    attribution_text?: string;
    allows_modification?: boolean;
    allows_commercial_use?: boolean;
    allows_distribution?: boolean;
    license_url?: string;
    rights_holder?: string;
    valid_until?: string;
    usage_notes?: string;
    is_organization_specific?: boolean;
    max_users?: number;
  };
}

interface SharedLiteratureBrowserProps {
  onSelect?: (literature: SharedLiterature) => void;
  studyPlanId?: string;
  mode?: 'browse' | 'select';
}

export function SharedLiteratureBrowser({
  onSelect,
  studyPlanId,
  mode = 'browse'
}: SharedLiteratureBrowserProps) {
  const [literature, setLiterature] = useState<SharedLiterature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [literatureType, setLiteratureType] = useState<string>('');
  const [hasArchive, setHasArchive] = useState(false);
  const [accessLevel, setAccessLevel] = useState<string>('');  // 'open', 'restricted', or ''
  const [orderBy, setOrderBy] = useState('usage_count');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<Record<string, {
    usage: Array<{
      link_id: string;
      study_plan_id: string;
      study_plan_title: string;
      study_plan_status: string;
      organization_name: string | null;
      study_association_name: string | null;
      linked_version_number: number;
      current_version_number: number;
      has_newer_version: boolean;
    }>;
    total_count: number;
    visible_count: number;
    hidden_count: number;
    loading: boolean;
  }>>({});
  const [stats, setStats] = useState<any>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  // License modal state
  const [selectedLiterature, setSelectedLiterature] = useState<SharedLiterature | null>(null);
  const [sharingRules, setSharingRules] = useState<SharingRulesResponse | null>(null);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showOwnLicenseModal, setShowOwnLicenseModal] = useState(false);
  const [addingToStudyPlan, setAddingToStudyPlan] = useState(false);

  // Track already-added literature IDs
  const [alreadyAddedIds, setAlreadyAddedIds] = useState<Set<string>>(new Set());

  // Current user organization (for edit/delete permissions)
  // currentUserOrgId beholdes for bakoverkompatibilitet, men eier-sjekk gjøres nå via canEditDelete
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_currentUserOrgId, setCurrentUserOrgId] = useState<string | null>(null);

  // Edit modal state
  const [editingLit, setEditingLit] = useState<SharedLiterature | null>(null);
  const [editForm, setEditForm] = useState({
    title: '',
    author: '',
    publisher: '',
    publication_year: '',
    original_url: '',
    description: '',
    isbn: '',
    doi: '',
    literature_type: 'webpage'
  });
  const [saving, setSaving] = useState(false);
  const [editContentMode, setEditContentMode] = useState<'keep' | 'upload' | 'fetch_url'>('keep');
  const [editFile, setEditFile] = useState<File | null>(null);
  const [editFetchUrl, setEditFetchUrl] = useState('');
  const [replacingContent, setReplacingContent] = useState(false);

  // Delete confirmation
  const [deletingLit, setDeletingLit] = useState<SharedLiterature | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Add new resource modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState<'link' | 'upload'>('link');
  const [archiveMode, setArchiveMode] = useState<'link' | 'archive'>('link');
  const [addForm, setAddForm] = useState({
    title: '',
    original_url: '',
    isbn: '',
    doi: '',
    author: '',
    publisher: '',
    publication_year: '',
    language: 'no',
    description: '',
    literature_type: 'webpage'
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [addingResource, setAddingResource] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Archived content viewer
  const [viewingArchive, setViewingArchive] = useState<SharedLiterature | null>(null);
  const [archiveInfo, setArchiveInfo] = useState<{
    file_exists: boolean;
    file_size: number;
    file_type: string;
    has_extracted_text: boolean;
    extracted_text_preview: string | null;
  } | null>(null);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [showExtractedText, setShowExtractedText] = useState(false);

  const fetchLiterature = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (literatureType) params.set('literature_type', literatureType);
      if (hasArchive) params.set('has_archive', 'true');
      if (accessLevel) params.set('access_level', accessLevel);
      params.set('order_by', orderBy);
      params.set('limit', String(pagination.limit));
      params.set('offset', String(reset ? 0 : pagination.offset));

      const response = await authenticatedFetch(`/api/shared-literature?${params}`);

      if (!response.ok) {
        throw new Error('Kunne ikke hente litteratur');
      }

      const data = await response.json();

      if (reset) {
        setLiterature(data.data);
      } else {
        setLiterature(prev => [...prev, ...data.data]);
      }
      setPagination(data.pagination);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, literatureType, hasArchive, orderBy, pagination.limit, pagination.offset]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/shared-literature/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLiterature(true);
    fetchStats();
  }, [search, literatureType, hasArchive, orderBy]);

  // Get current user's member organization
  useEffect(() => {
    const user = getUser();
    if (user) {
      setCurrentUserOrgId(user.member_id || null);
    }
  }, []);

  // Fetch already-added literature IDs when in select mode
  useEffect(() => {
    if (mode === 'select' && studyPlanId) {
      const fetchAlreadyAdded = async () => {
        try {
          const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`);
          if (response.ok) {
            const data = await response.json();
            const ids = new Set<string>(
              data.literature
                .filter((lit: any) => lit.shared_literature_id)
                .map((lit: any) => lit.shared_literature_id)
            );
            setAlreadyAddedIds(ids);
          }
        } catch (err) {
          console.error('Failed to fetch already added literature:', err);
        }
      };
      fetchAlreadyAdded();
    }
  }, [mode, studyPlanId]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLiterature(true);
  };

  const handleLoadMore = () => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
    fetchLiterature(false);
  };

  // Check sharing rules before adding literature
  const checkSharingRules = async (lit: SharedLiterature): Promise<SharingRulesResponse | null> => {
    try {
      const response = await authenticatedFetch('/api/literature/check-sharing-rules', {
        method: 'POST',
        body: JSON.stringify({
          shared_literature_id: lit.id
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke sjekke delingsregler');
      }

      const data = await response.json();
      // Backend returns sharing rules directly in response, not wrapped in data
      return data as SharingRulesResponse;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  // Main handler for add button click
  const handleAddToStudyPlan = async (lit: SharedLiterature) => {
    if (!studyPlanId) return;

    setSelectedLiterature(lit);
    setError(null);

    // Check sharing rules
    const rules = await checkSharingRules(lit);
    if (!rules) return;

    setSharingRules(rules);

    // Determine which modal to show based on rules
    if (rules.requiresOwnLicense) {
      // Closed license - user must provide their own license
      setShowOwnLicenseModal(true);
    } else if (rules.requiresDisclaimer) {
      // Open license - show disclaimer
      setShowDisclaimerModal(true);
    } else if (rules.canShare) {
      // Direct add (rare edge case - e.g., public domain)
      await doAddToStudyPlan(lit, {
        disclaimer_accepted: true,
        inherit_license: rules.inheritLicense
      });
    } else {
      // Cannot share at all
      setError(rules.reason || 'Denne litteraturen kan ikke deles.');
    }
  };

  // Actually add the literature to study plan
  const doAddToStudyPlan = async (
    lit: SharedLiterature,
    options: {
      disclaimer_accepted?: boolean;
      own_license?: LicenseFormData;
      inherit_license?: boolean;
    }
  ) => {
    if (!studyPlanId) return;

    setAddingToStudyPlan(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature/link`, {
        method: 'POST',
        body: JSON.stringify({
          shared_literature_id: lit.id,
          disclaimer_accepted: options.disclaimer_accepted || false,
          inherit_license: options.inherit_license || false,
          own_license: options.own_license || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke legge til litteratur');
      }

      // Success - close modals, update state, and notify parent
      setShowDisclaimerModal(false);
      setShowOwnLicenseModal(false);
      setSelectedLiterature(null);
      setSharingRules(null);

      // Mark this item as already added
      setAlreadyAddedIds(prev => new Set([...prev, lit.id]));

      if (onSelect) {
        onSelect(lit);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAddingToStudyPlan(false);
    }
  };

  // Handler for disclaimer modal accept
  const handleDisclaimerAccept = () => {
    if (!selectedLiterature) return;
    doAddToStudyPlan(selectedLiterature, {
      disclaimer_accepted: true,
      inherit_license: sharingRules?.inheritLicense || false
    });
  };

  // Handler for own license modal confirm
  const handleOwnLicenseConfirm = (ownLicense: LicenseFormData) => {
    if (!selectedLiterature) return;
    doAddToStudyPlan(selectedLiterature, {
      own_license: ownLicense
    });
  };

  // Handler for modal cancel
  const handleModalCancel = () => {
    setShowDisclaimerModal(false);
    setShowOwnLicenseModal(false);
    setSelectedLiterature(null);
    setSharingRules(null);
  };

  // Sjekk om bruker kan redigere/slette ressursen.
  // Eierskap-modell (matcher backend):
  //   - Systemadmin: alltid
  //   - Studieforbund-admin: hvis ressursens eier-medlem tilhører eget forbund
  //   - Medlem (uavhengig av admin-status): hvis bruker tilhører eier-medlem
  //   - Ellers: nei
  const canEditDelete = (lit: SharedLiterature): boolean => {
    const u = getUser();
    if (!u) return false;
    if (u.role === 'systemadmin') return true;
    if (u.role === 'admin') {
      return !!lit.owner_study_association_id
        && lit.owner_study_association_id === u.study_association_id;
    }
    if (u.role === 'member') {
      return !!lit.owner_member_id
        && lit.owner_member_id === (u as any).member_id;
    }
    return false;
  };

  // Pre-edit advarsel før edit-modal når ressursen er i bruk
  const [preEditWarning, setPreEditWarning] = useState<SharedLiterature | null>(null);

  const handleEditClick = (lit: SharedLiterature) => {
    if (lit.link_count > 0) {
      // Last bruks-info hvis ikke allerede lastet
      loadUsage(lit.id);
      setPreEditWarning(lit);
    } else {
      handleEdit(lit);
    }
  };

  // Open edit modal
  const handleEdit = (lit: SharedLiterature) => {
    setPreEditWarning(null);
    setEditingLit(lit);
    setEditForm({
      title: lit.title || '',
      author: lit.author || '',
      publisher: lit.publisher || '',
      publication_year: lit.publication_year?.toString() || '',
      original_url: lit.original_url || '',
      description: lit.description || '',
      isbn: lit.isbn || '',
      doi: lit.doi || '',
      literature_type: lit.literature_type || 'webpage'
    });
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editingLit) return;
    setSaving(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/shared-literature/${editingLit.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title: editForm.title,
          author: editForm.author || null,
          publisher: editForm.publisher || null,
          publication_year: editForm.publication_year ? parseInt(editForm.publication_year) : null,
          original_url: editForm.original_url || null,
          description: editForm.description || null,
          isbn: editForm.isbn || null,
          doi: editForm.doi || null,
          literature_type: editForm.literature_type
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke oppdatere');
      }

      // Hvis brukeren også har valgt å bytte filinnhold, kjør replace-content-flyten
      if (editContentMode === 'upload' || editContentMode === 'fetch_url') {
        setReplacingContent(true);
        try {
          let replaceResp: Response;
          if (editContentMode === 'upload') {
            if (!editFile) throw new Error('Velg en fil for opplasting');
            const fd = new FormData();
            fd.append('file', editFile);
            replaceResp = await authenticatedFetch(`/api/shared-literature/${editingLit.id}/replace-content`, {
              method: 'POST', body: fd
            });
          } else {
            if (!editFetchUrl.trim()) throw new Error('Skriv inn URL å hente fra');
            replaceResp = await authenticatedFetch(`/api/shared-literature/${editingLit.id}/replace-content`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ archive_url: editFetchUrl.trim() })
            });
          }
          const replaceData = await replaceResp.json();
          if (!replaceResp.ok || !replaceData.success) {
            throw new Error(replaceData.error || 'Filbytte feilet');
          }
        } finally {
          setReplacingContent(false);
        }
      }

      setEditingLit(null);
      setEditContentMode('keep');
      setEditFile(null);
      setEditFetchUrl('');
      fetchLiterature(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Confirm delete
  const handleDelete = async () => {
    if (!deletingLit) return;
    setDeleting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/shared-literature/${deletingLit.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke slette');
      }

      setDeletingLit(null);
      fetchLiterature(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // View archived content
  const handleViewArchive = async (lit: SharedLiterature) => {
    setViewingArchive(lit);
    setArchiveInfo(null);
    setExtractedText(null);
    setShowExtractedText(false);
    setLoadingArchive(true);

    try {
      const response = await authenticatedFetch(`/api/shared-literature/${lit.id}/archived-content?format=info`);
      if (response.ok) {
        const data = await response.json();
        setArchiveInfo(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch archive info:', err);
    } finally {
      setLoadingArchive(false);
    }
  };

  // Load extracted text
  const handleLoadExtractedText = async () => {
    if (!viewingArchive) return;
    setLoadingArchive(true);

    try {
      const response = await authenticatedFetch(`/api/shared-literature/${viewingArchive.id}/extracted-text`);
      if (response.ok) {
        const data = await response.json();
        setExtractedText(data.data.extracted_text);
        setShowExtractedText(true);
      }
    } catch (err) {
      console.error('Failed to fetch extracted text:', err);
    } finally {
      setLoadingArchive(false);
    }
  };

  const loadUsage = async (litId: string) => {
    if (usageData[litId] && !usageData[litId].loading) return;
    setUsageData(prev => ({ ...prev, [litId]: { usage: [], total_count: 0, visible_count: 0, hidden_count: 0, loading: true } }));
    try {
      const res = await authenticatedFetch(`/api/shared-literature/${litId}/usage`);
      const data = await res.json();
      if (res.ok && data.success) {
        setUsageData(prev => ({
          ...prev,
          [litId]: {
            usage: data.usage || [],
            total_count: data.total_count || 0,
            visible_count: data.visible_count || 0,
            hidden_count: data.hidden_count || 0,
            loading: false
          }
        }));
      } else {
        setUsageData(prev => ({ ...prev, [litId]: { usage: [], total_count: 0, visible_count: 0, hidden_count: 0, loading: false } }));
      }
    } catch {
      setUsageData(prev => ({ ...prev, [litId]: { usage: [], total_count: 0, visible_count: 0, hidden_count: 0, loading: false } }));
    }
  };

  const handleToggleExpand = (litId: string) => {
    if (expandedId === litId) {
      setExpandedId(null);
    } else {
      setExpandedId(litId);
      loadUsage(litId);
    }
  };

  const resetAddForm = () => {
    setAddForm({
      title: '',
      original_url: '',
      isbn: '',
      doi: '',
      author: '',
      publisher: '',
      publication_year: '',
      language: 'no',
      description: '',
      literature_type: 'webpage'
    });
    setUploadFile(null);
    setAddMode('link');
    setAddError(null);
  };

  const handleAddResource = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);

    if (!addForm.title.trim()) {
      setAddError('Tittel er påkrevd');
      return;
    }

    setAddingResource(true);
    try {
      let response: Response;

      if (addMode === 'upload') {
        if (!uploadFile) {
          setAddError('Velg en fil å laste opp');
          setAddingResource(false);
          return;
        }
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('title', addForm.title.trim());
        fd.append('language', addForm.language);
        if (addForm.isbn.trim()) fd.append('isbn', addForm.isbn.trim());
        if (addForm.doi.trim()) fd.append('doi', addForm.doi.trim());
        if (addForm.author.trim()) fd.append('author', addForm.author.trim());
        if (addForm.publisher.trim()) fd.append('publisher', addForm.publisher.trim());
        if (addForm.publication_year.trim()) fd.append('publication_year', addForm.publication_year.trim());
        if (addForm.description.trim()) fd.append('description', addForm.description.trim());

        response = await authenticatedFetch('/api/shared-literature/upload', {
          method: 'POST',
          body: fd
        });
      } else {
        if (!addForm.original_url.trim() && !addForm.isbn.trim() && !addForm.doi.trim()) {
          setAddError('Minst én av URL, ISBN eller DOI må fylles ut');
          setAddingResource(false);
          return;
        }
        const payload: Record<string, unknown> = {
          title: addForm.title.trim(),
          literature_type: addForm.literature_type,
          language: addForm.language,
          archive_mode: archiveMode
        };
        if (addForm.original_url.trim()) payload.original_url = addForm.original_url.trim();
        if (addForm.isbn.trim()) payload.isbn = addForm.isbn.trim();
        if (addForm.doi.trim()) payload.doi = addForm.doi.trim();
        if (addForm.author.trim()) payload.author = addForm.author.trim();
        if (addForm.publisher.trim()) payload.publisher = addForm.publisher.trim();
        if (addForm.publication_year.trim()) {
          const year = parseInt(addForm.publication_year.trim(), 10);
          if (!isNaN(year) && year > 1900 && year < 2100) payload.publication_year = year;
        }
        if (addForm.description.trim()) payload.description = addForm.description.trim();

        response = await authenticatedFetch('/api/shared-literature', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();
      if (!response.ok || !data.success) {
        setAddError(data.error || 'Kunne ikke opprette ressurs');
        return;
      }

      setShowAddModal(false);
      resetAddForm();
      await fetchLiterature(true);
      await fetchStats();
    } catch (err: any) {
      setAddError(err.message || 'Ukjent feil');
    } finally {
      setAddingResource(false);
    }
  };

  // Open PDF in new tab — henter via authenticatedFetch og åpner blob-URL
  // (vanlig window.open mister auth-header og gir 401)
  const handleOpenPdf = async () => {
    if (!viewingArchive) return;
    try {
      const response = await authenticatedFetch(
        `/api/shared-literature/${viewingArchive.id}/archived-content`
      );
      if (!response.ok) {
        alert('Kunne ikke hente PDF — er du fortsatt innlogget?');
        return;
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank');
      // Frigjør URL etter at fanen har lastet ferdig (ca. 60s)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      if (!win) {
        alert('Pop-up blokkert. Tillat pop-ups for denne siden for å åpne PDF.');
      }
    } catch (err: any) {
      console.error('Open PDF error:', err);
      alert(`Kunne ikke åpne PDF: ${err.message || err}`);
    }
  };

  // Last ned arkivert fil
  const handleDownloadArchived = async () => {
    if (!viewingArchive) return;
    try {
      const response = await authenticatedFetch(
        `/api/shared-literature/${viewingArchive.id}/archived-content`
      );
      if (!response.ok) {
        alert('Kunne ikke laste ned filen');
        return;
      }
      const blob = await response.blob();
      const ext = archiveInfo?.file_type || 'pdf';
      const safeTitle = (viewingArchive.title || 'litteratur')
        .replace(/[^a-zA-Z0-9æøåÆØÅ\-_ ]/g, '_').slice(0, 80);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${safeTitle}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5_000);
    } catch (err: any) {
      console.error('Download error:', err);
      alert(`Kunne ikke laste ned: ${err.message || err}`);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'word':
        return <FileText className="w-5 h-5 text-blue-500" />;
      case 'webpage':
        return <Globe className="w-5 h-5 text-green-500" />;
      case 'video':
        return <Video className="w-5 h-5 text-purple-500" />;
      default:
        return <Book className="w-5 h-5 text-gray-500" />;
    }
  };

  const getDownloadStatusBadge = (status: string) => {
    switch (status) {
      case 'downloaded':
        return (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 cursor-help"
            title="Filen er lastet ned og arkivert lokalt. Kan vises selv om original-URL ikke er tilgjengelig."
          >
            <Archive className="w-3 h-3" />
            Arkivert
          </span>
        );
      case 'pending':
        return (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 cursor-help"
            title="Ikke arkivert lokalt — ressursen er kun en lenke til original-URL. Typisk for forsider og levende nettsider (SSB, DSB osv.) som ikke skal lastes ned."
          >
            <Clock className="w-3 h-3" />
            Venter
          </span>
        );
      case 'failed':
        return (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 cursor-help"
            title="Forsøk på å laste ned filen feilet. Original-URL brukes som fallback. Kontakt admin hvis filen burde vært arkivert."
          >
            <AlertCircle className="w-3 h-3" />
            Feilet
          </span>
        );
      case 'unavailable':
        return (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700 cursor-help"
            title="Original-URL er ikke lenger tilgjengelig (404 eller borte). Vurder å erstatte ressursen."
          >
            <AlertCircle className="w-3 h-3" />
            Utilgjengelig
          </span>
        );
      case 'not_needed':
        return (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 cursor-help"
            title="Manuell ressurs (bok, video, ekstern tjeneste) — skal ikke arkiveres som fil."
          >
            <Book className="w-3 h-3" />
            Lenke
          </span>
        );
      default:
        return null;
    }
  };

  const getAccessLevelBadge = (accessLevel: string, licenseType: string | null, ccVariant: string | null) => {
    if (accessLevel === 'restricted') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
          <AlertCircle className="w-3 h-3" />
          Begrenset tilgang
        </span>
      );
    }
    if (licenseType && licenseType !== 'unknown') {
      const label = licenseType === 'creative_commons'
        ? (ccVariant ? `CC ${ccVariant.replace('CC-', '')}` : 'CC-lisens')
        : licenseType === 'public_domain' ? 'Offentlig domene'
        : licenseType === 'open_access' ? 'Åpen tilgang'
        : licenseType === 'citation_right' ? 'Sitatrett'
        : licenseType === 'kopinor_agreement' ? 'Kopinor'
        : licenseType === 'proprietary' ? 'Proprietær'
        : licenseType === 'subscription' ? 'Abonnement'
        : licenseType === 'purchased' ? 'Kjøpt'
        : licenseType === 'educational' ? 'Utdanning'
        : licenseType;

      const isOpen = ['public_domain', 'creative_commons', 'open_access', 'citation_right'].includes(licenseType);

      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
          isOpen ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
        }`}>
          <Shield className="w-3 h-3" />
          {label}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
        <AlertCircle className="w-3 h-3" />
        Ikke spesifisert
      </span>
    );
  };

  // Get license permission badges
  const getLicensePermissionBadges = (lit: SharedLiterature) => {
    if (!lit.license_type || lit.license_type === 'unknown') return null;

    const badges = [];
    if (lit.allows_distribution === true) {
      badges.push(<span key="dist" className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Kan deles</span>);
    } else if (lit.allows_distribution === false) {
      badges.push(<span key="dist" className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded">Kan ikke deles</span>);
    }
    if (lit.allows_modification === false) {
      badges.push(<span key="mod" className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Ingen endring</span>);
    }
    if (lit.allows_commercial_use === false) {
      badges.push(<span key="com" className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Ikke-kommersiell</span>);
    }
    if (lit.requires_attribution) {
      badges.push(<span key="attr" className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Kreditering</span>);
    }

    return badges.length > 0 ? <div className="flex flex-wrap gap-1 mt-1">{badges}</div> : null;
  };

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      {mode === 'browse' && stats && (
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-100">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <Library className="w-6 h-6 text-indigo-600" />
              <h2 className="text-lg font-semibold text-gray-900">Delt litteraturbibliotek</h2>
            </div>
            <button
              onClick={() => { resetAddForm(); setShowAddModal(true); }}
              className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 shadow-sm"
              title="Legg til ny ressurs i biblioteket"
            >
              <Plus className="w-4 h-4" />
              Legg til ressurs
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500">Totalt</div>
              <div className="font-semibold text-gray-900">{stats.total || 0} kilder</div>
            </div>
            <div>
              <div className="text-gray-500">Arkivert</div>
              <div className="font-semibold text-green-600">{stats.archived || 0} filer</div>
            </div>
            <div>
              <div className="text-gray-500">Bruk</div>
              <div className="font-semibold text-gray-900">{stats.total_usage || 0} ganger</div>
            </div>
          </div>
        </div>
      )}

      {/* Search and filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søk i tittel, forfatter, utgiver..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 border rounded-lg flex items-center gap-1 ${
              showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-300 text-gray-700'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filter
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Søk
          </button>
        </form>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={literatureType}
                onChange={(e) => setLiteratureType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Alle typer</option>
                <option value="pdf">PDF</option>
                <option value="word">Word</option>
                <option value="webpage">Nettside</option>
                <option value="video">Video</option>
                <option value="manual">Manuell</option>
                <option value="other">Annet</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tilgang</label>
              <select
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Alle</option>
                <option value="open">Åpen (kan deles)</option>
                <option value="restricted">Begrenset (lisensbeskyttet)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sortering</label>
              <select
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="usage_count">Mest brukt</option>
                <option value="created_at">Nyeste først</option>
                <option value="title">Alfabetisk</option>
                <option value="quality_rating">Høyest kvalitet</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasArchive}
                  onChange={(e) => setHasArchive(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Kun arkiverte</span>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Literature list */}
      <div className="space-y-2">
        {literature.map((lit) => (
          <div
            key={lit.id}
            className="bg-white rounded-lg border border-gray-200 hover:border-indigo-300 transition-colors"
          >
            <div className="p-4">
              <div className="flex items-start gap-3">
                {getTypeIcon(lit.literature_type)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-medium text-gray-900 truncate">{lit.title}</h3>
                      {lit.author && (
                        <p className="text-sm text-gray-500">{lit.author}</p>
                      )}
                      {/* Eier-info — autoritativ for tilgang og kontakt */}
                      <p className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                        <Building2 className="w-3 h-3" />
                        <span className="font-medium">Eid av:</span>
                        {lit.owner_member_name ? (
                          <>
                            <span>{lit.owner_member_name}</span>
                            {lit.owner_study_association_name && (
                              <span className="text-gray-400">({lit.owner_study_association_name})</span>
                            )}
                            {lit.owner_contact_email && (
                              <a
                                href={`mailto:${lit.owner_contact_email}?subject=${encodeURIComponent('Tilbakemelding på delt litteratur: ' + lit.title)}`}
                                className="text-indigo-600 hover:underline"
                                title="Kontakt eier-organisasjonen"
                              >
                                {lit.owner_contact_email}
                              </a>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-500">Systemadmin (Kompetanseforbundet)</span>
                        )}
                        {canEditDelete(lit) && (
                          <span className="text-indigo-600 font-medium ml-1">— du kan redigere</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        Versjon {lit.current_version_number}
                        {lit.link_count > 0 && (
                          <span className="ml-2">· brukes i {lit.link_count} {lit.link_count === 1 ? 'studieplan' : 'studieplaner'}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <div className="flex items-center gap-1.5 flex-wrap justify-end">
                        {getDownloadStatusBadge(lit.download_status)}
                        {getAccessLevelBadge(lit.access_level, lit.license_type, lit.cc_variant)}
                      </div>
                      <div className="flex items-center gap-1">
                        {mode === 'browse' && canEditDelete(lit) && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleEditClick(lit)}
                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                              title={lit.link_count > 0
                                ? `Rediger (vises advarsel om ${lit.link_count} berørt${lit.link_count === 1 ? ' studieplan' : 'e studieplaner'} først)`
                                : 'Rediger'}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {lit.link_count === 0 ? (
                              <button
                                type="button"
                                onClick={() => setDeletingLit(lit)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Slett"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              <span
                                className="p-1.5 text-gray-300 cursor-help"
                                title="Sletting er deaktivert fordi ressursen brukes i studieplaner. Fjern koblingene først hvis du vil slette."
                              >
                                <Trash2 className="w-4 h-4" />
                              </span>
                            )}
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggleExpand(lit.id)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title={expandedId === lit.id ? 'Skjul detaljer' : 'Vis detaljer'}
                        >
                          {expandedId === lit.id ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* License permission badges */}
                  {getLicensePermissionBadges(lit)}

                  <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                    {lit.usage_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {lit.usage_count} studieplan{lit.usage_count !== 1 && 'er'}
                      </span>
                    )}
                    {lit.quality_rating && (
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        {lit.quality_rating}/5
                      </span>
                    )}
                    {lit.publication_year && (
                      <span>{lit.publication_year}</span>
                    )}
                    {lit.original_url && (
                      <a
                        href={lit.original_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                        Åpne
                      </a>
                    )}
                    {lit.download_status === 'downloaded' && lit.local_file_path && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewArchive(lit);
                        }}
                        className="flex items-center gap-1 text-green-600 hover:text-green-800"
                      >
                        <Eye className="w-4 h-4" />
                        Se arkiv
                      </button>
                    )}
                  </div>

                  {/* Expandable details */}
                  {expandedId === lit.id && (
                    <div className="mt-3 pt-3 border-t border-gray-100 text-sm">
                      {lit.description && (
                        <p className="text-gray-600 mb-2">{lit.description}</p>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-gray-500">
                        {lit.publisher && (
                          <div>
                            <span className="font-medium">Utgiver:</span> {lit.publisher}
                          </div>
                        )}
                        {lit.isbn && (
                          <div>
                            <span className="font-medium">ISBN:</span> {lit.isbn}
                          </div>
                        )}
                        {lit.doi && (
                          <div>
                            <span className="font-medium">DOI:</span> {lit.doi}
                          </div>
                        )}
                        {lit.language && (
                          <div>
                            <span className="font-medium">Språk:</span> {lit.language}
                          </div>
                        )}
                      </div>
                      {lit.categories && lit.categories.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {lit.categories.map((cat, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Bruks-info — hvilke studieplaner bruker denne ressursen */}
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <p className="font-medium text-gray-700 mb-2">Brukes i studieplaner</p>
                        {usageData[lit.id]?.loading ? (
                          <p className="text-xs text-gray-400">Laster…</p>
                        ) : usageData[lit.id] ? (
                          usageData[lit.id].visible_count === 0 && usageData[lit.id].hidden_count === 0 ? (
                            <p className="text-xs text-gray-500 italic">Ingen studieplaner bruker denne ressursen ennå.</p>
                          ) : (
                            <>
                              <ul className="space-y-1.5">
                                {usageData[lit.id].usage.map(u => (
                                  <li key={u.link_id} className="flex items-center justify-between gap-2 text-xs bg-gray-50 px-3 py-2 rounded">
                                    <div className="min-w-0">
                                      <a
                                        href={`/study-plans/${u.study_plan_id}`}
                                        className="font-medium text-indigo-700 hover:underline truncate block"
                                      >
                                        {u.study_plan_title}
                                      </a>
                                      <span className="text-gray-500">
                                        {u.organization_name && <span>{u.organization_name}</span>}
                                        {u.organization_name && u.study_association_name && <span> · </span>}
                                        {u.study_association_name && <span>{u.study_association_name}</span>}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                                        u.has_newer_version
                                          ? 'bg-amber-100 text-amber-800'
                                          : 'bg-green-100 text-green-800'
                                      }`} title={u.has_newer_version ? `Bruker versjon ${u.linked_version_number} av ${u.current_version_number}` : 'Bruker siste versjon'}>
                                        v{u.linked_version_number}
                                        {u.has_newer_version && ` / ${u.current_version_number}`}
                                      </span>
                                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                                        u.study_plan_status === 'approved' ? 'bg-green-100 text-green-700'
                                        : u.study_plan_status === 'draft' ? 'bg-gray-100 text-gray-700'
                                        : 'bg-blue-100 text-blue-700'
                                      }`}>
                                        {u.study_plan_status === 'approved' ? 'Godkjent'
                                         : u.study_plan_status === 'draft' ? 'Utkast'
                                         : u.study_plan_status === 'submitted' ? 'Innsendt'
                                         : u.study_plan_status === 'under_review' ? 'Under vurdering'
                                         : u.study_plan_status === 'rejected' ? 'Avvist'
                                         : u.study_plan_status}
                                      </span>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                              {usageData[lit.id].hidden_count > 0 && (
                                <p className="mt-2 text-xs text-gray-500 italic">
                                  + {usageData[lit.id].hidden_count} {usageData[lit.id].hidden_count === 1 ? 'studieplan' : 'studieplaner'} i andre organisasjoner som du ikke har tilgang til å se.
                                </p>
                              )}
                            </>
                          )
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>

                {/* "Legg til i studieplan"-knapp i select-modus (egen knapp, ikke dupli-action) */}
                {mode === 'select' && studyPlanId && (
                  <div className="flex items-center gap-2">
                    {alreadyAddedIds.has(lit.id) ? (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-sm">
                        <CheckCircle className="w-4 h-4" />
                        <span>Lagt til</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAddToStudyPlan(lit)}
                        disabled={addingToStudyPlan}
                        className="p-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400"
                        title="Legg til i studieplan"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {literature.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            <Library className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Ingen litteratur funnet</p>
            <p className="text-sm">Prøv å justere søkekriteriene</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
          </div>
        )}

        {pagination.hasMore && !loading && (
          <button
            onClick={handleLoadMore}
            className="w-full py-3 text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Last inn flere ({pagination.total - literature.length} gjenstår)
          </button>
        )}
      </div>

      {/* License Disclaimer Modal - for open licenses */}
      {showDisclaimerModal && selectedLiterature && sharingRules?.license && (
        <LicenseDisclaimerModal
          literature={{
            id: selectedLiterature.id,
            title: selectedLiterature.title,
            author: selectedLiterature.author || undefined,
            original_url: selectedLiterature.original_url || undefined,
            publisher: selectedLiterature.publisher || undefined
          }}
          license={{
            license_type: sharingRules.license.license_type as any,
            cc_variant: sharingRules.license.cc_variant as any,
            requires_attribution: sharingRules.license.requires_attribution,
            attribution_text: sharingRules.license.attribution_text,
            allows_modification: sharingRules.license.allows_modification,
            allows_commercial_use: sharingRules.license.allows_commercial_use,
            allows_distribution: sharingRules.license.allows_distribution,
            license_url: sharingRules.license.license_url,
            rights_holder: sharingRules.license.rights_holder,
            valid_until: sharingRules.license.valid_until,
            usage_notes: sharingRules.license.usage_notes
          }}
          onAccept={handleDisclaimerAccept}
          onCancel={handleModalCancel}
          isLoading={addingToStudyPlan}
        />
      )}

      {/* Own License Required Modal - for closed licenses */}
      {showOwnLicenseModal && selectedLiterature && (
        <OwnLicenseRequiredModal
          literature={{
            id: selectedLiterature.id,
            title: selectedLiterature.title,
            author: selectedLiterature.author || undefined,
            original_url: selectedLiterature.original_url || undefined,
            publisher: selectedLiterature.publisher || undefined
          }}
          originalLicense={sharingRules?.license ? {
            license_type: sharingRules.license.license_type as any,
            cc_variant: sharingRules.license.cc_variant as any,
            valid_until: sharingRules.license.valid_until,
            is_organization_specific: sharingRules.license.is_organization_specific,
            max_users: sharingRules.license.max_users
          } : null}
          reason={sharingRules?.reason}
          onConfirm={handleOwnLicenseConfirm}
          onCancel={handleModalCancel}
          isLoading={addingToStudyPlan}
        />
      )}

      {/* Edit Modal */}
      {/* Pre-edit-advarsel når ressursen er i bruk */}
      {preEditWarning && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setPreEditWarning(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-200 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Ressursen er i bruk</h3>
                <p className="text-sm text-gray-600 mt-0.5">{preEditWarning.title}</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                Denne ressursen brukes i <strong>{preEditWarning.link_count}</strong>{' '}
                {preEditWarning.link_count === 1 ? 'studieplan' : 'studieplaner'}.
                Endringer oppretter en ny versjon (v{preEditWarning.current_version_number + 1}).
                Eksisterende koblinger blir <strong>låst til nåværende versjon (v{preEditWarning.current_version_number})</strong>{' '}
                — brukerne får varsel om at en nyere versjon er tilgjengelig og kan velge å oppgradere
                eller beholde sin versjon.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <p className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">
                  Berørte studieplaner
                </p>
                {usageData[preEditWarning.id]?.loading ? (
                  <p className="text-xs text-gray-500">Laster…</p>
                ) : usageData[preEditWarning.id] ? (
                  usageData[preEditWarning.id].visible_count === 0 && usageData[preEditWarning.id].hidden_count === 0 ? (
                    <p className="text-xs text-gray-500 italic">Ingen detaljer tilgjengelig.</p>
                  ) : (
                    <>
                      <ul className="space-y-1.5 text-xs">
                        {usageData[preEditWarning.id].usage.map(u => (
                          <li key={u.link_id} className="flex items-center justify-between gap-2 bg-white px-3 py-2 rounded border border-gray-100">
                            <span className="font-medium text-gray-900 truncate">{u.study_plan_title}</span>
                            <span className="text-gray-500 flex-shrink-0">
                              {u.organization_name || '—'}
                              {u.study_plan_status === 'approved' && <span className="ml-2 text-green-700">(godkjent)</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                      {usageData[preEditWarning.id].hidden_count > 0 && (
                        <p className="mt-2 text-xs text-gray-500 italic">
                          + {usageData[preEditWarning.id].hidden_count} i andre organisasjoner du ikke har tilgang til
                        </p>
                      )}
                    </>
                  )
                ) : null}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
                Brukerne av disse studieplanene vil få varsel om at en nyere versjon er tilgjengelig
                når de neste gang åpner sin litteraturliste. De kan da:
                <ul className="mt-1 ml-5 list-disc text-xs">
                  <li>Oppgradere koblingen til ny versjon</li>
                  <li>Beholde sin opprinnelige versjon</li>
                  <li>Lage en egen kopi av ressursen for sin organisasjon</li>
                </ul>
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setPreEditWarning(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Avbryt
              </button>
              <button
                onClick={() => handleEdit(preEditWarning)}
                className="px-4 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-dark"
              >
                Fortsett til redigering
              </button>
            </div>
          </div>
        </div>
      )}

      {editingLit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Rediger litteratur</h3>
              <button
                onClick={() => setEditingLit(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tittel *</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forfatter</label>
                <input
                  type="text"
                  value={editForm.author}
                  onChange={(e) => setEditForm({ ...editForm, author: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utgiver</label>
                  <input
                    type="text"
                    value={editForm.publisher}
                    onChange={(e) => setEditForm({ ...editForm, publisher: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utgivelsesår</label>
                  <input
                    type="number"
                    value={editForm.publication_year}
                    onChange={(e) => setEditForm({ ...editForm, publication_year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="1900"
                    max="2100"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="url"
                  value={editForm.original_url}
                  onChange={(e) => setEditForm({ ...editForm, original_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ISBN</label>
                  <input
                    type="text"
                    value={editForm.isbn}
                    onChange={(e) => setEditForm({ ...editForm, isbn: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">DOI</label>
                  <input
                    type="text"
                    value={editForm.doi}
                    onChange={(e) => setEditForm({ ...editForm, doi: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={editForm.literature_type}
                  onChange={(e) => setEditForm({ ...editForm, literature_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="webpage">Nettside</option>
                  <option value="pdf">PDF</option>
                  <option value="word">Word</option>
                  <option value="video">Video</option>
                  <option value="manual">Manuell</option>
                  <option value="other">Annet</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Endre filinnhold */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filinnhold</label>
                {editingLit?.local_file_path ? (
                  <p className="text-xs text-gray-600 mb-2">
                    Nåværende: <span className="font-medium">arkivert lokalt</span>
                    {' '}(versjon {editingLit.current_version_number})
                  </p>
                ) : (
                  <p className="text-xs text-gray-600 mb-2">
                    Nåværende: <span className="font-medium">kun lenke</span>, ingen lokal fil
                  </p>
                )}
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="edit-content-mode" value="keep"
                      checked={editContentMode === 'keep'}
                      onChange={() => setEditContentMode('keep')}
                      className="mt-1" />
                    <span className="text-sm">Behold nåværende</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="edit-content-mode" value="upload"
                      checked={editContentMode === 'upload'}
                      onChange={() => setEditContentMode('upload')}
                      className="mt-1" />
                    <span className="text-sm">
                      <span className="font-medium">Last opp ny fil</span>
                      <span className="block text-xs text-gray-500">PDF, Word, PowerPoint, EPUB, HTML, TXT (maks 32 MB). Skannes med VirusTotal før lagring.</span>
                    </span>
                  </label>
                  {editContentMode === 'upload' && (
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.epub,.html,.htm,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/epub+zip,text/html,text/plain"
                      onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                      className="ml-6 w-[calc(100%-1.5rem)] px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  )}
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="edit-content-mode" value="fetch_url"
                      checked={editContentMode === 'fetch_url'}
                      onChange={() => setEditContentMode('fetch_url')}
                      className="mt-1" />
                    <span className="text-sm">
                      <span className="font-medium">Hent fra (ny) URL og arkiver</span>
                      <span className="block text-xs text-gray-500">Henter filen fra URL, skanner og lagrer lokalt. Kan ta 30–90 sek.</span>
                    </span>
                  </label>
                  {editContentMode === 'fetch_url' && (
                    <input
                      type="url"
                      placeholder="https://eksempel.no/dokument.pdf"
                      value={editFetchUrl}
                      onChange={(e) => setEditFetchUrl(e.target.value)}
                      className="ml-6 w-[calc(100%-1.5rem)] px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  )}
                </div>
                {editContentMode !== 'keep' && (
                  <p className="text-xs text-amber-700 mt-2">
                    Endring av filinnhold oppretter ny versjon. Eksisterende koblinger fryses til sin opprinnelige versjon — brukerne kan oppgradere senere.
                  </p>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setEditingLit(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Avbryt
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || replacingContent || !editForm.title.trim()
                  || (editContentMode === 'upload' && !editFile)
                  || (editContentMode === 'fetch_url' && !editFetchUrl.trim())}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
              >
                {replacingContent ? 'Skanner og lagrer fil…' : saving ? 'Lagrer…' : 'Lagre'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingLit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Slett litteratur</h3>
            </div>
            <div className="p-4">
              <p className="text-gray-700 mb-2">
                Er du sikker på at du vil slette denne litteraturen fra det delte biblioteket?
              </p>
              <p className="font-medium text-gray-900 mb-4">"{deletingLit.title}"</p>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>Merk:</strong> Denne handlingen kan ikke angres.
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setDeletingLit(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Avbryt
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
              >
                {deleting ? 'Sletter...' : 'Slett'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archived Content Viewer Modal */}
      {viewingArchive && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Archive className="w-5 h-5 text-green-600" />
                  Arkivert innhold
                </h3>
                <p className="text-sm text-gray-500 mt-1">{viewingArchive.title}</p>
              </div>
              <button
                onClick={() => {
                  setViewingArchive(null);
                  setArchiveInfo(null);
                  setExtractedText(null);
                  setShowExtractedText(false);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
              {loadingArchive ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : archiveInfo ? (
                <div className="space-y-4">
                  {/* File info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Filinformasjon</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Filtype:</span>
                        <span className="ml-2 font-medium">{archiveInfo.file_type.toUpperCase()}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Størrelse:</span>
                        <span className="ml-2 font-medium">{formatFileSize(archiveInfo.file_size)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Fil tilgjengelig:</span>
                        <span className={`ml-2 font-medium ${archiveInfo.file_exists ? 'text-green-600' : 'text-red-600'}`}>
                          {archiveInfo.file_exists ? 'Ja' : 'Nei'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Ekstrahert tekst:</span>
                        <span className={`ml-2 font-medium ${archiveInfo.has_extracted_text ? 'text-green-600' : 'text-gray-400'}`}>
                          {archiveInfo.has_extracted_text ? 'Tilgjengelig' : 'Ikke tilgjengelig'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    {archiveInfo.file_exists && archiveInfo.file_type === 'pdf' && (
                      <button
                        onClick={handleOpenPdf}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      >
                        <Eye className="w-4 h-4" />
                        Åpne PDF
                      </button>
                    )}
                    {archiveInfo.file_exists && (
                      <button
                        onClick={handleDownloadArchived}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        <Download className="w-4 h-4" />
                        Last ned
                      </button>
                    )}
                    {archiveInfo.has_extracted_text && !showExtractedText && (
                      <button
                        onClick={handleLoadExtractedText}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        <FileText className="w-4 h-4" />
                        Vis ekstrahert tekst
                      </button>
                    )}
                  </div>

                  {/* Preview */}
                  {archiveInfo.extracted_text_preview && !showExtractedText && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">Tekstforhåndsvisning</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {archiveInfo.extracted_text_preview}
                      </p>
                    </div>
                  )}

                  {/* Full extracted text */}
                  {showExtractedText && extractedText && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Ekstrahert tekst</h4>
                        <button
                          onClick={() => setShowExtractedText(false)}
                          className="text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          Skjul
                        </button>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                          {extractedText}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Warning if file doesn't exist */}
                  {!archiveInfo.file_exists && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800">
                        <strong>Advarsel:</strong> Den arkiverte filen ble ikke funnet på serveren.
                        Filen kan ha blitt slettet eller flyttet.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Kunne ikke laste arkivinformasjon</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setViewingArchive(null);
                  setArchiveInfo(null);
                  setExtractedText(null);
                  setShowExtractedText(false);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add new resource modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !addingResource && setShowAddModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleAddResource}>
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Legg til ny ressurs i biblioteket</h2>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  disabled={addingResource}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Tab-velger: lenke vs. opplasting */}
                <div className="flex gap-2 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => setAddMode('link')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      addMode === 'link'
                        ? 'border-indigo-600 text-indigo-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Lenke (URL/ISBN/DOI)
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddMode('upload')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      addMode === 'upload'
                        ? 'border-indigo-600 text-indigo-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Last opp fil
                  </button>
                </div>

                {addMode === 'link' ? (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-900">
                      Ressursen lenkes til en ekstern URL eller identifikator. Minst én av URL,
                      ISBN eller DOI må fylles ut.
                    </div>
                    <div className="border border-gray-200 rounded-md p-3 space-y-2">
                      <p className="text-sm font-medium text-gray-700">Hva skal skje med URL-en?</p>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name="archive-mode" value="link"
                          checked={archiveMode === 'link'}
                          onChange={() => setArchiveMode('link')}
                          className="mt-1" />
                        <span className="text-sm">
                          <span className="font-medium">Bare lenke</span>
                          <span className="block text-xs text-gray-600">Brukerne åpner URL-en direkte. Anbefalt for forsider og levende nettsider.</span>
                        </span>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio" name="archive-mode" value="archive"
                          checked={archiveMode === 'archive'}
                          onChange={() => setArchiveMode('archive')}
                          className="mt-1" />
                        <span className="text-sm">
                          <span className="font-medium">Arkiver fil lokalt</span>
                          <span className="block text-xs text-gray-600">
                            Filen hentes fra URL og skannes med VirusTotal før lagring.
                            Ressursen er da tilgjengelig selv om URL-en blir død.
                            <br />
                            <em>Tar typisk 30–90 sekunder ved første registrering.</em>
                          </span>
                        </span>
                      </label>
                    </div>
                  </>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
                    <strong>Sikkerhetsskanning:</strong> Filen sendes til VirusTotal for
                    skanning før den lagres lokalt. Skanningen tar typisk 10–60 sekunder.
                    Filer som blir flagget som ondsinnet eller mistenkelig blir avvist.
                    Maks 32 MB. Tillatte typer: PDF, Word, PowerPoint, EPUB, HTML, TXT.
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tittel *</label>
                  <input
                    type="text"
                    value={addForm.title}
                    onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {addMode === 'upload' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Velg fil *</label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.epub,.html,.htm,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/epub+zip,text/html,text/plain"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      required={addMode === 'upload'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    {uploadFile && (
                      <p className="mt-1 text-xs text-gray-600">
                        {uploadFile.name} — {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {addMode === 'link' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={addForm.literature_type}
                        onChange={(e) => setAddForm({ ...addForm, literature_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="webpage">Nettside</option>
                        <option value="pdf">PDF</option>
                        <option value="word">Word-dokument</option>
                        <option value="video">Video</option>
                        <option value="manual">Bok/manuell</option>
                        <option value="other">Annet</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Språk</label>
                    <select
                      value={addForm.language}
                      onChange={(e) => setAddForm({ ...addForm, language: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="no">Norsk</option>
                      <option value="en">Engelsk</option>
                      <option value="se">Samisk</option>
                      <option value="other">Annet</option>
                    </select>
                  </div>
                </div>

                {addMode === 'link' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                    <input
                      type="url"
                      value={addForm.original_url}
                      onChange={(e) => setAddForm({ ...addForm, original_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ISBN</label>
                    <input
                      type="text"
                      value={addForm.isbn}
                      onChange={(e) => setAddForm({ ...addForm, isbn: e.target.value })}
                      placeholder="978-..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">DOI</label>
                    <input
                      type="text"
                      value={addForm.doi}
                      onChange={(e) => setAddForm({ ...addForm, doi: e.target.value })}
                      placeholder="10.xxxx/..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Forfatter</label>
                    <input
                      type="text"
                      value={addForm.author}
                      onChange={(e) => setAddForm({ ...addForm, author: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Utgiver</label>
                    <input
                      type="text"
                      value={addForm.publisher}
                      onChange={(e) => setAddForm({ ...addForm, publisher: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Utgivelsesår</label>
                  <input
                    type="number"
                    min="1900"
                    max="2099"
                    value={addForm.publication_year}
                    onChange={(e) => setAddForm({ ...addForm, publication_year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivelse</label>
                  <textarea
                    value={addForm.description}
                    onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                    rows={3}
                    placeholder="Kort om ressursen — hva dekker den, og når egner den seg som litteratur?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {addError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                    {addError}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  disabled={addingResource}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={addingResource}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {addingResource
                    ? (addMode === 'upload' ? 'Skanner og lagrer…' : 'Lagrer…')
                    : 'Legg til ressurs'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
